// sagas/banLinkageSaga.ts
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  fetchBanDetailsAPI,
  initiateLinkCardAPI,
  validateSelectedAccountAPI,
  linkSelectedAccountAPI,
  generateOtpAPI,
  submitOtpAPI,
  unlinkAccountAPI,
  fetchRCIAccountStatus,
  fetchRCIAccount,
  fetchRewarduser,
} from '../api';
import {
  getBanDetailsRequest,
  getBanDetailsSuccess,
  getBanDetailsFailed,
  fetchRCIAccountStatusRequest,
  fetchRCIAccountStatusSuccess,
  fetchRCIAccountStatusFailed,
  fetchRCIAccountRequest,
  fetchRCIAccountSuccess,
  fetchRCIAccountFailed,
  fetchRewardAccountRequest,
  fetchRewardAccountSuccess,
  fetchRewardAccountFailed,
  initiateLinkCardRequest,
  initiateLinkCardSuccess,
  initiateLinkCardFailed,
  validateSelectedAccountRequest,
  validateSelectedAccountSuccess,
  validateSelectedAccountFailed,
  linkSelectedAccountRequest,
  linkSelectedAccountSuccess,
  linkSelectedAccountFailed,
  generateOtpRequest,
  generateOtpSuccess,
  generateOtpFailed,
  submitOtpRequest,
  submitOtpSuccess,
  submitOtpFailed,
  unlinkAccountRequest,
  unlinkAccountSuccess,
  unlinkAccountFailed,
} from '../reducer/banLinkageSlice';

// Interface definitions for the action types
interface GetBanDetailsAction {
  type: typeof getBanDetailsRequest.type;
  payload: {
    ban: string;
    accountId: string;
    isRefresh?: boolean;
  };
}

// New interface for FetchRCIAccountStatus
interface FetchRCIAccountStatusAction {
  type: typeof fetchRCIAccountStatusRequest.type;
  payload: {
    accountId?: string;
  };
}

interface FetchRCIAccountAction {
  type: typeof fetchRCIAccountRequest.type;
  payload: {
    accountId?: string;
  };
}

interface FetchRewardAccountAction {
  type: typeof fetchRewardAccountRequest.type;
  payload: {
    user_id: string;
  };
}

interface InitiateLinkCardAction {
  type: typeof initiateLinkCardRequest.type;
  payload: {
    appCode: string;
    accountId: string;
    customerId: string;
    ban: string;
  };
}

interface ValidateSelectedAccountAction {
  type: typeof validateSelectedAccountRequest.type;
  payload: {
    rciAccount: string;
    accountId: string;
    customerId: string;
  };
}

interface LinkSelectedAccountAction {
  type: typeof linkSelectedAccountRequest.type;
  payload: {
    ban: string;
    accountId: string;
    customerId: string;
  };
}

interface GenerateOtpAction {
  type: typeof generateOtpRequest.type;
  payload: {
    accountId: string;
    mobileNumber: string;
    language: string;
    recaptchaToken: string;
  };
}

interface SubmitOtpAction {
  type: typeof submitOtpRequest.type;
  payload: {
    accountId: string;
    mobileNumber: string;
    otp: string;
    recaptchaToken: string;
  };
}

interface UnlinkAccountAction {
  type: typeof unlinkAccountRequest.type;
  payload: {
    ban: string;
    accountId: string;
    customerId: string;
  };
}

// BAN Details Saga
export function* getBanDetailsSaga(
  action: GetBanDetailsAction
): Generator<any, void, unknown> {
  try {
    const { ban, accountId, isRefresh } = action.payload;

    const banDetails: any = yield call(
      fetchBanDetailsAPI,
      'getBanDetails',
      ban,
      accountId,
      isRefresh
    );

    yield put(getBanDetailsSuccess(banDetails));
  } catch (error: any) {
    yield put(getBanDetailsFailed(error.message || 'Something went wrong'));
  }
}

export function* fetchRCIAccountStatusSaga(
  action: FetchRCIAccountStatusAction
): Generator<any, void, unknown> {
  try {
    const { accountId } = action.payload;

    const accountStatus: any = yield call(
      fetchRCIAccountStatus,
      'fetchRCIAccountStatus',
      accountId
    );

    yield put(fetchRCIAccountStatusSuccess(accountStatus));
  } catch (error: any) {
    yield put(
      fetchRCIAccountStatusFailed(error.message || 'Something went wrong')
    );
  }
}

export function* fetchRCIAccountSaga(
  action: FetchRCIAccountAction
): Generator<any, void, unknown> {
  try {
    const { accountId } = action.payload;

    const accountStatus: any = yield call(
      fetchRCIAccount,
      'fetchRCIAccount',
      accountId
    );

    yield put(fetchRCIAccountSuccess(accountStatus));
  } catch (error: any) {
    yield put(fetchRCIAccountFailed(error.message || 'Something went wrong'));
  }
}

export function* fetchRewardAccountSaga(
  action: FetchRewardAccountAction
): Generator<any, void, unknown> {
  try {
    const { user_id } = action.payload;
    const accountStatus: any = yield call(
      fetchRewarduser,
      'fetchRewarduser',
      user_id
    );
    yield put(fetchRewardAccountSuccess(accountStatus));
  } catch (error: any) {
    yield put(
      fetchRewardAccountFailed(error.message || 'Something went wrong')
    );
  }
}

// Initiate Link Card Saga
export function* initiateLinkCardSaga(
  action: InitiateLinkCardAction
): Generator<any, void, unknown> {
  try {
    const linkCardDetails: any = yield call(
      initiateLinkCardAPI,
      'initiate_link_card',
      action.payload
    );

    yield put(initiateLinkCardSuccess(linkCardDetails));
  } catch (error: any) {
    yield put(initiateLinkCardFailed(error.message || 'Something went wrong'));
  }
}

// Validate Selected Account Saga
export function* validateSelectedAccountSaga(
  action: ValidateSelectedAccountAction
): Generator<any, void, unknown> {
  try {
    const { rciAccount, accountId, customerId } = action.payload;

    const payload = { rciAccount, accountId, customerId };

    const response: any = yield call(
      validateSelectedAccountAPI,
      'validateSelectedAccount',
      payload
    );

    const validationDetails = response ?? {};

    yield put(validateSelectedAccountSuccess(validationDetails));
  } catch (error: any) {
    yield put(
      validateSelectedAccountFailed(error.message || 'Something went wrong')
    );
  }
}

// Link Selected Account Saga
export function* linkSelectedAccountSaga(
  action: LinkSelectedAccountAction
): Generator<any, void, unknown> {
  try {
    const { ban, accountId, customerId } = action.payload;

    const payload = { ban, accountId, customerId };

    const linkDetails: any = yield call(
      linkSelectedAccountAPI,
      'linkSelectedAccount',
      payload
    );

    console.log('[linkSelectedAccountSaga] linkDetails received:', linkDetails);

    // Check if this is an error response (status 409 wrapped in 500)
    if (linkDetails?.status === '409' || linkDetails?.status === 409) {
      console.log('[linkSelectedAccountSaga] Detected 409 error in response');
      console.log('[linkSelectedAccountSaga] Detail:', linkDetails.detail);
      // Pass to success handler with the error details - reducer will set correct indicator
      yield put(linkSelectedAccountSuccess(linkDetails));
    } else if (linkDetails?.statusCode === 500 && linkDetails?.error) {
      console.log('[linkSelectedAccountSaga] Detected 500 error without proper detail');
      // This is a generic error, pass to failed handler
      yield put(linkSelectedAccountFailed({
        error: linkDetails.error.errCode || 'UNKNOWN_ERROR',
        detail: linkDetails.error.msg,
        status: String(linkDetails.statusCode),
        title: 'Error'
      }));
    } else {
      // Normal success response
      console.log('[linkSelectedAccountSaga] Success response');
      yield put(linkSelectedAccountSuccess(linkDetails));
    }
  } catch (error: any) {
    console.log('[linkSelectedAccountSaga] Caught exception:', error);
    yield put(
      linkSelectedAccountFailed({
        error: error.message || 'Something went wrong',
        detail: error.message,
        status: '500',
        title: 'Error'
      })
    );
  }
}

// Generate OTP Saga
export function* generateOtpSaga(
  action: GenerateOtpAction
): Generator<any, void, unknown> {
  try {
    const { accountId, mobileNumber, language, recaptchaToken } =
      action.payload;

    const payload = {
      accountId,
      mobileNumber,
      language,
      recaptchaToken,
    };

    const otpDetails: any = yield call(generateOtpAPI, 'generateOtp', payload);

    yield put(generateOtpSuccess(otpDetails));
  } catch (error: any) {
    yield put(generateOtpFailed(error.message || 'Something went wrong'));
  }
}

// Submit OTP Saga
export function* submitOtpSaga(
  action: SubmitOtpAction
): Generator<any, void, unknown> {
  try {
    const { accountId, mobileNumber, otp, recaptchaToken } = action.payload;

    const payload = {
      accountId,
      mobileNumber,
      otp,
      recaptchaToken,
    };

    const submitDetails: any = yield call(submitOtpAPI, 'submitOtp', payload);
    if (submitDetails?.status === true) {
      yield put(submitOtpSuccess(submitDetails));
    } else {
      yield put(submitOtpFailed(submitDetails));
    }
  } catch (error: any) {
    yield put(submitOtpFailed(error.message || 'Something went wrong'));
  }
}

// Unlink Account Saga
export function* unlinkAccountSaga(
  action: UnlinkAccountAction
): Generator<any, void, unknown> {
  try {
    const { ban, accountId, customerId } = action.payload;

    const payload = { ban, accountId, customerId };

    const unlinkDetails: any = yield call(
      unlinkAccountAPI,
      'unlinkAccount',
      payload
    );

    if (
      unlinkDetails?.status === 'Success' ||
      unlinkDetails?.status === '200' ||
      unlinkDetails?.status === 200 ||
      unlinkDetails?.status === '201' ||
      unlinkDetails?.status === 201
    ) {
      yield put(unlinkAccountSuccess(unlinkDetails));
    } else {
      yield put(unlinkAccountFailed(unlinkDetails));
    }
  } catch (error: any) {
    yield put(unlinkAccountFailed(error.message || 'Something went wrong'));
  }
}

// Root saga to watch all BAN linkage related actions
export function* watchBanLinkageSaga() {
  yield takeLatest(getBanDetailsRequest.type, getBanDetailsSaga);
  yield takeLatest(
    fetchRCIAccountStatusRequest.type,
    fetchRCIAccountStatusSaga
  );
  yield takeLatest(fetchRCIAccountRequest.type, fetchRCIAccountSaga);
  yield takeLatest(fetchRewardAccountRequest.type, fetchRewardAccountSaga);
  yield takeLatest(initiateLinkCardRequest.type, initiateLinkCardSaga);
  yield takeLatest(
    validateSelectedAccountRequest.type,
    validateSelectedAccountSaga
  );
  yield takeLatest(linkSelectedAccountRequest.type, linkSelectedAccountSaga);
  yield takeLatest(generateOtpRequest.type, generateOtpSaga);
  yield takeLatest(submitOtpRequest.type, submitOtpSaga);
  yield takeLatest(unlinkAccountRequest.type, unlinkAccountSaga);
}

export default watchBanLinkageSaga;
